import React from "react";
import { Link } from "react-router-dom";
import { ChartLineUp, Lightning, TrendUp, Database, ArrowRight } from "@phosphor-icons/react";
import { useGlobalData } from "@/lib/dataStreams";

const ModuleCard = ({ title, desc, link, icon: Icon, colorClass, bgClass, iconColorClass, number }) => (
  <Link to={link} className={`rounded-2xl border border-slate-800 bg-slate-900/50 hover:bg-slate-800 hover:border-${colorClass} transition-all duration-300 p-8 group relative overflow-hidden`}>
    <div className="flex justify-between items-start mb-6 relative z-10">
      <div className={`w-12 h-12 rounded-xl ${bgClass} flex items-center justify-center group-hover:scale-110 transition-all`}>
        <Icon size={24} className={iconColorClass} />
      </div>
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 border border-slate-700 bg-slate-800/50 px-3 py-1 rounded-full">
        Module {number}
      </span>
    </div>
    <h2 className="text-2xl font-bold text-slate-100 mb-3 relative z-10">{title}</h2>
    <p className="text-slate-300 text-sm leading-relaxed mb-6 relative z-10">{desc}</p>
    <div className={`text-sm font-semibold ${iconColorClass} relative z-10 flex items-center gap-1 group-hover:translate-x-1 transition-transform`}>
      Launch Module <ArrowRight size={14} weight="bold" />
    </div>
  </Link>
);

const MasterModuleCard = () => (
  <Link to="/bot/ai-combo" className="rounded-2xl border border-slate-700 bg-gradient-to-br from-slate-800 to-slate-900 hover:border-purple-400 transition-all duration-300 p-8 group relative overflow-hidden shadow-lg shadow-purple-900/10">
    <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none" />
    <div className="flex justify-between items-start mb-6 relative z-10">
      <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center group-hover:scale-110 transition-all">
        <Lightning size={24} className="text-purple-300" />
      </div>
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-900 bg-slate-200 px-3 py-1 rounded-full shadow-sm">
        Module 04
      </span>
    </div>
    <h2 className="text-2xl font-bold text-slate-100 mb-3 relative z-10">AI Synergy</h2>
    <p className="text-slate-300 text-sm leading-relaxed mb-6 relative z-10">Dynamic allocation between Arbitrage, Migration, and Momentum strategies based on macro signals.</p>
    <div className="text-sm font-semibold text-purple-300 relative z-10 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
      Launch Console <ArrowRight size={14} weight="bold" />
    </div>
  </Link>
);

const NetworkStatus = ({ price }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 flex justify-between items-center shadow-inner">
    <div>
      <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1.5">Network Status</div>
      <div className="text-emerald-400 text-sm font-medium flex items-center gap-2">
        <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.8)]" /> Solana Mainnet Connected
      </div>
    </div>
    <div className="text-right">
      <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1.5">Live SOL/USD</div>
      <div className="font-mono text-2xl font-bold text-slate-100">{price ? `$${price.toFixed(3)}` : "Loading..."}</div>
    </div>
  </div>
);

export default function Dashboard() {
  const { solPrice: price } = useGlobalData();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-indigo-500/30 pt-10 pb-20">
      <div className="max-w-[1500px] mx-auto px-4 md:px-8">
        <div className="mb-12">
          <span className="text-indigo-400 font-bold text-xs uppercase tracking-wider">// Command Center</span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mt-3 text-slate-100">
            Bot Management <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">Terminal</span>
          </h1>
          <p className="text-slate-300 mt-4 max-w-2xl text-base leading-relaxed">
            Welcome to the B3eye showcase environment. This dashboard provides read-only access to our suite of advanced trading algorithms operating across the Solana blockchain.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <ModuleCard
            title="Arbitrage"
            desc="Exploiting price inefficiencies across 11 CEXs and Solana DEXs in real-time without market exposure."
            link="/bot/arbitrage"
            icon={TrendUp}
            colorClass="indigo-500/50"
            bgClass="bg-indigo-500/10"
            iconColorClass="text-indigo-400"
            number="01"
          />
          <ModuleCard
            title="Pump Sniper"
            desc="Detecting bonding curve completions and sniping early liquidity block-0 on Raydium/PumpSwap."
            link="/bot/pump-migration"
            icon={Database}
            colorClass="teal-500/50"
            bgClass="bg-teal-500/10"
            iconColorClass="text-teal-400"
            number="02"
          />
          <ModuleCard
            title="AI Momentum"
            desc="Processing order flow to execute high-probability SOL ↔ USDC swing trades ahead of retail volume."
            link="/bot/ai-trade"
            icon={ChartLineUp}
            colorClass="rose-500/50"
            bgClass="bg-rose-500/10"
            iconColorClass="text-rose-400"
            number="03"
          />
          <MasterModuleCard />
        </div>

        <NetworkStatus price={price} />
      </div>
    </div>
  );
}
